import { Action } from "redux-saga";
import { selectIsKioskOrderingActive } from "../../../kiosk/navigation/selectors";
import { isNewOrderScreenActive } from "../../../utils/navigation";
import {
  ENGINE_V2,
  clearRecommendations,
  decideV1CartAction,
  fetchRecommendations,
  getRecommendationEngineVersion,
  isSuggestiveSellingEnabled,
  selectRecommendationId,
  selectRecommenderConfig,
  shouldFetchV2OnCartChange,
} from "../../../recommendations";

/**
 * `saleUpdate/recommendationEffect` — the CART TRIGGER for suggestive selling.
 *
 * SHARED, with no platform variants at all. It used to be web-only, with the other platform
 * carrying a smaller inline copy in its middleware — no `RISTA_FBT` branch, no item OPTIONS in
 * the id set, and no "don't re-trigger on an item that was itself recommended" guard. That copy
 * is gone; this is the one definition, and both platforms run both engine versions.
 *
 * All the actual RULES live in `recommendations/request` as pure functions. What is left here is
 * the listener adapter: pull the two cart snapshots off `listenerApi`, ask, dispatch. That is why
 * this file has no branching worth testing and `request.ts` has all of it.
 */

/**
 * Is a surface that can actually SHOW suggestions the one on screen?
 *
 * The ordering screen has to be the one SHOWING, not merely mounted. Both platforms keep this
 * listener attached while something is layered over that screen — web's checkout/discount/
 * customer-info routes, mobile's cart, channel-switch and delivery/dine-in screens — and all of
 * those dispatch `saveSaleData` too. Without the guard the engine is re-queried from the payment
 * screen, where nothing can show the answer.
 *
 * There are TWO such surfaces, and this is the only place that knows both:
 *
 * | surface | how it answers |
 * |---|---|
 * | POS ordering screen | `utils/navigation`'s `isNewOrderScreenActive()` — platform-split, each app from its own router |
 * | kiosk CATALOG / CART | `kiosk/navigation`'s `selectIsKioskOrderingActive` — one selector, off the route mirror in `kioskStore` |
 *
 * ⚠️ The kiosk half is why this is not a bare `isNewOrderScreenActive()` any more. On a kiosk the
 * router is sitting on `/kiosk/catalog` (web) or the kiosk stack (mobile), so the POS predicate is
 * `false` on every screen a customer can reach — and the V2 engine, which is the default flow, was
 * therefore never queried for a kiosk order at all. The V1 path has no screen guard and was
 * unaffected, which is why this looked like it worked.
 *
 * The kiosk is asked FIRST and from state: on a kiosk device the POS predicate can only ever be
 * false, and a state read cannot disagree with the route guard that reads the same field.
 */
function isOrderingSurfaceActive(state: any): boolean {
  return selectIsKioskOrderingActive(state) || isNewOrderScreenActive();
}

/** V1: the engine is asked "what goes with the item that was just added". */
function recommendationEffectV1(_: Action, listenerApi: any) {
  const currentState = listenerApi.getState();
  const prevState = listenerApi.getOriginalState();

  const action = decideV1CartAction({
    recommenderConfig: selectRecommenderConfig(currentState),
    prevSaleItems: prevState?.saleDataStore?.saleData?.saleItems || [],
    currentSaleItems: currentState?.saleDataStore?.saleData?.saleItems || [],
    currentRecommendationId: selectRecommendationId(currentState),
    selectedChannelData: currentState?.saleDataStore?.selectedChannelData,
  });

  if (action.type === "CLEAR") {
    clearRecommendations(listenerApi.dispatch);
    return;
  }
  if (action.type === "FETCH_V1") {
    fetchRecommendations(listenerApi.dispatch, { v1Payload: action.payload });
  }
}

/** V2: the engine is asked about the guest and the whole cart, on either changing. */
function recommendationEffectV2(_: Action, listenerApi: any) {
  const currentState = listenerApi.getState();
  const prevState = listenerApi.getOriginalState();

  if (!isSuggestiveSellingEnabled(currentState)) {
    return;
  }

  if (!isOrderingSurfaceActive(currentState)) {
    return;
  }

  const prevSaleData = prevState?.saleDataStore?.saleData;
  const currentSaleData = currentState?.saleDataStore?.saleData;

  if (!shouldFetchV2OnCartChange({ prevSaleData, currentSaleData })) {
    return;
  }

  fetchRecommendations(listenerApi.dispatch, {
    saleData: currentSaleData,
    customerStore: currentState?.customerStore,
  });
}

export const recommendationEffect = async (action: Action, listenerApi: any) => {
  if (getRecommendationEngineVersion() === ENGINE_V2) {
    recommendationEffectV2(action, listenerApi);
  } else {
    recommendationEffectV1(action, listenerApi);
  }
};
